import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Star,
  Calendar,
  Clock,
  ArrowLeft,
  Play,
  User,
  Clapperboard,
  HardDrive,
  FileVideo
} from "lucide-react";

export const MovieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMovie = async () => {
      try {
        const response = await axios.get(`${API}/movies/${id}`);
        setMovie(response.data);
      } catch (error) {
        console.error("Error fetching movie:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMovie();
  }, [id]);

  const handleActorClick = (actorName) => {
    navigate(`/search?actor=${encodeURIComponent(actorName)}`);
  };

  const handleDirectorClick = (directorName) => {
    navigate(`/search?director=${encodeURIComponent(directorName)}`);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "Inconnu";
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb >= 1) return `${gb.toFixed(2)} Go`;
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} Mo`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="pt-24 text-center">
          <h1 className="text-2xl text-white">Film non trouvé</h1>
          <Link to="/search" className="text-violet-400 hover:underline mt-4 inline-block">
            Retour à la recherche
          </Link>
        </div>
      </div>
    );
  }

  const backdropUrl = movie.backdrop_path || movie.poster_path || "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1920";
  const posterUrl = movie.poster_path || "https://via.placeholder.com/300x450/171717/7c3aed?text=No+Poster";

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="movie-detail">
      <Navbar />

      {/* Hero Backdrop */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={backdropUrl}
          alt={movie.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="relative -mt-48 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-6 text-gray-400 hover:text-white"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Poster */}
            <div className="flex-shrink-0">
              <img
                src={posterUrl}
                alt={movie.title}
                className="w-64 mx-auto lg:mx-0 rounded-xl shadow-2xl border border-white/10"
                data-testid="movie-poster"
              />
            </div>

            {/* Info */}
            <div className="flex-1">
              {/* Title */}
              <h1
                className="font-['Outfit'] text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4"
                data-testid="movie-title"
              >
                {movie.title}
              </h1>

              {movie.original_title && movie.original_title !== movie.title && (
                <p className="text-gray-400 text-lg mb-4 italic">
                  {movie.original_title}
                </p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {movie.vote_average > 0 && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 border border-yellow-500/30">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="text-yellow-400 font-semibold">
                      {movie.vote_average.toFixed(1)}
                    </span>
                  </div>
                )}

                {movie.release_date && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>{movie.release_date}</span>
                  </div>
                )}

                {movie.runtime > 0 && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>{movie.runtime} min</span>
                  </div>
                )}
              </div>

              {/* Genres */}
              {movie.genres?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {movie.genres.map((genre, idx) => (
                    <Badge
                      key={idx}
                      variant="outline"
                      className="bg-violet-500/10 border-violet-500/30 text-violet-300"
                    >
                      {genre}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Director */}
              {movie.director && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Clapperboard className="w-4 h-4" />
                    <span className="text-sm">Réalisateur</span>
                  </div>
                  <button
                    onClick={() => handleDirectorClick(movie.director)}
                    className="text-white hover:text-violet-400 transition-colors font-medium"
                    data-testid="director-link"
                  >
                    {movie.director}
                  </button>
                </div>
              )}

              {/* Overview */}
              {movie.overview && (
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-white mb-2">Synopsis</h2>
                  <p className="text-gray-300 leading-relaxed" data-testid="movie-overview">
                    {movie.overview}
                  </p>
                </div>
              )}

              {/* File Info */}
              {movie.file_info && (
                <div className="glass rounded-xl p-4 mb-8">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <FileVideo className="w-4 h-4 text-violet-400" />
                    Informations du fichier
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Nom</span>
                      <p className="text-gray-300 truncate font-mono text-xs">
                        {movie.file_info.name}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Taille</span>
                      <p className="text-gray-300 flex items-center gap-1">
                        <HardDrive className="w-3 h-3" />
                        {formatFileSize(movie.file_info.size)}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Modifié</span>
                      <p className="text-gray-300">
                        {movie.file_info.modified_date
                          ? new Date(movie.file_info.modified_date).toLocaleDateString("fr-FR")
                          : "Inconnu"}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cast Section */}
          {movie.actors?.length > 0 && (
            <div className="mt-12">
              <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-6 flex items-center gap-2">
                <User className="w-6 h-6 text-violet-500" />
                Distribution
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {movie.actors.map((actor, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleActorClick(actor.name)}
                    className="actor-card glass rounded-xl p-4 text-left transition-all cursor-pointer"
                    data-testid={`actor-${idx}`}
                  >
                    <div className="flex items-center gap-3">
                      {actor.profile_path ? (
                        <img
                          src={actor.profile_path}
                          alt={actor.name}
                          className="w-12 h-12 rounded-full object-cover border border-white/10"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-violet-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate text-sm">
                          {actor.name}
                        </p>
                        {actor.character && (
                          <p className="text-gray-500 text-xs truncate">
                            {actor.character}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
